import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import { parseDataBlock } from "../utils/dataBlock";

type Value = { text: string } | { columns: string[]; rows: string[][] };

/** A live value on a page. Until it loads, or if the source can't answer, the
 *  reader sees the page's static fallback, so a source going down breaks nothing. */
export default function DataBlock({ raw, live = true, renderFallback }: { raw: string; live?: boolean; renderFallback?: (md: string) => ReactNode }) {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { source, key, fallback } = useMemo(() => parseDataBlock(raw), [raw]);
  const [value, setValue] = useState<Value | null>(null);

  useEffect(() => {
    setValue(null);
    if (!live || !projectSlug || !source) return;
    let cancelled = false;
    const qs = key ? `?key=${encodeURIComponent(key)}` : "";
    authFetch(`/api/docs/${projectSlug}/data/${encodeURIComponent(source)}${qs}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setValue(d); })
      .catch(() => { /* keep the fallback */ });
    return () => { cancelled = true; };
  }, [live, projectSlug, source, key]);

  if (value && "columns" in value) {
    return (
      <div className="overflow-x-auto my-4">
        <table>
          <thead><tr>{value.columns.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
          <tbody>{value.rows.map((r, i) => <tr key={i}>{r.map((v, j) => <td key={j}>{v}</td>)}</tr>)}</tbody>
        </table>
      </div>
    );
  }
  if (value && "text" in value) return <p className="whitespace-pre-wrap">{value.text}</p>;
  return renderFallback ? <>{renderFallback(fallback)}</> : <p className="whitespace-pre-wrap">{fallback}</p>;
}
